/**
 * Quick Add to Cart functionality for product cards
 * Shows size selector and add to cart button on hover (desktop)
 * Shows modal on mobile when clicking icon
 */
(function() {
    'use strict';

    // Modal elements (lazy-initialized to ensure DOM is ready)
    let modal = null;
    let modalOverlay = null;
    let modalClose = null;
    let modalHeader = null;
    let modalTitle = null;
    let modalSizesContainer = null;
    let modalAddBtn = null;
    let modalInitialized = false;
    
    let currentSelectedVariantId = null;
    
    // How long the desktop "added to cart" confirmation stays on the card image
    const ADDED_TOAST_TIMEOUT = 4000;
    const ADDED_TOAST_ICON = '<svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="11" cy="11" r="11" fill="#106572"/><path d="M6 11.5L9.5 15L16 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    const scriptSrc = document.currentScript ? document.currentScript.src : '';
    
    // Load desktop hover styles (bfs-quick-add.css). The URL comes from the card
    // markup so it carries Shopify's asset version for cache busting.
    function loadQuickAddStyles() {
        if (document.querySelector('link[data-bfs-quick-add-css]')) return;
        const source = document.querySelector('[data-quick-add-css]');
        const href = source ? source.getAttribute('data-quick-add-css') : scriptSrc.replace('bfs-quick-add.js', 'bfs-quick-add.css');
        if (!href || href.indexOf('bfs-quick-add.css') === -1) return;
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = href;
        link.setAttribute('data-bfs-quick-add-css', '');
        document.head.appendChild(link);
    }
    
    // Boost filter renders grid cards from its own JS template (boost-pfs-filter.js),
    // so bring that markup in line with bfs-product-card.liquid
    function normaliseQuickAddMarkup(container) {
        const sizesContainer = container.querySelector('.bfs-product-card__quick-add-sizes');
        if (!sizesContainer) return;
        const buttons = sizesContainer.querySelectorAll('.bfs-product-card__quick-add-size');
        
        if (container.getAttribute('data-is-personalised') === 'true') {
            const title = container.querySelector('.bfs-product-card__quick-add-title');
            if (title) title.remove();
            sizesContainer.classList.add('bfs-product-card__quick-add-sizes--wide');
            buttons.forEach(function(btn) {
                if (btn.hasAttribute('data-personalise-choice')) return;
                const choice = btn.textContent.trim().toLowerCase();
                if (choice === 'yes') {
                    btn.setAttribute('data-personalise-choice', 'yes');
                    btn.textContent = 'Personalise';
                } else if (choice === 'no') {
                    btn.setAttribute('data-personalise-choice', 'no');
                    btn.classList.add('bfs-product-card__quick-add-size--primary');
                    btn.textContent = 'Add To Cart';
                }
            });
            return;
        }
        
        // "S - D16cm x H14cm": the chip shows the code; the full label is kept for the
        // tooltip, the added message and the mobile sheet
        buttons.forEach(function(btn) {
            if (btn.hasAttribute('data-label-full')) return;
            const label = btn.textContent.trim();
            const split = label.indexOf(' - ');
            if (split > 0) {
                btn.setAttribute('data-label-full', label);
                btn.setAttribute('title', label);
                btn.setAttribute('aria-label', label);
                btn.textContent = label.slice(0, split);
            }
        });

        // Text labels (Small, Queen...) share three columns; short sizes sit in a six-column grid
        const isWide = Array.prototype.some.call(buttons, function(btn) {
            return btn.textContent.trim().length > 3;
        });
        sizesContainer.classList.toggle('bfs-product-card__quick-add-sizes--wide', isWide);
    }

    function getFullLabel(btn) {
        return btn.getAttribute('data-label-full') || btn.textContent.trim();
    }
    
    function ensureModalElements() {
        if (modalInitialized) return !!modal;
        modal = document.getElementById('bfs-quick-add-modal');
        if (!modal) return false;
        modalOverlay = modal.querySelector('.bfs-quick-add-modal__overlay');
        modalClose = modal.querySelector('.bfs-quick-add-modal__close');
        modalHeader = modal.querySelector('.bfs-quick-add-modal__header');
        modalTitle = modalHeader ? modalHeader.querySelector('.bfs-quick-add-modal__title') : null;
        modalSizesContainer = document.getElementById('bfs-quick-add-modal-sizes');
        modalAddBtn = document.getElementById('bfs-quick-add-modal-add-btn');
        modalInitialized = true;
        
        // Attach modal event listeners once found
        if (modalClose) {
            modalClose.addEventListener('click', function(e) {
                e.preventDefault();
                closeModal();
            });
        }
        if (modalOverlay) {
            modalOverlay.addEventListener('click', function(e) {
                e.preventDefault();
                closeModal();
            });
        }
        if (modalAddBtn) {
            modalAddBtn.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                
                if (!currentSelectedVariantId || this.disabled || this.classList.contains('is-adding')) {
                    return;
                }
                
                var btn = this;
                addToCart(currentSelectedVariantId, btn, false).then(function() {
                    setTimeout(function() {
                        closeModal();
                        btn.textContent = 'Add to cart';
                        btn.style.background = '';
                        btn.disabled = false;
                        btn.classList.remove('is-added', 'is-adding');
                    }, 1500);
                }).catch(function(error) {
                    console.error('Error adding to cart:', error);
                });
            });
        }
        
        return !!modal;
    }
    
    // Open modal with product data
    function openModal(productHandle, productData) {
        // Store product handle for later use
        if (!productHandle && productData && productData.handle) {
            productHandle = productData.handle;
        }
        // Ensure modal elements are resolved
        ensureModalElements();
        if (!modal || !modalSizesContainer || !modalAddBtn) {
            console.error('Modal elements not found');
            return;
        }
        
        currentSelectedVariantId = null;
        
        // Clear previous sizes
        modalSizesContainer.innerHTML = '';
        
        // Check if product is personalized first
        let isPersonalised = false;
        let personalisationOptionIndex = -1;
        
        // Check product tags
        if (productData.tags && Array.isArray(productData.tags)) {
            isPersonalised = productData.tags.some(function(tag) {
                return tag && (tag.includes('_badge:Personalise') || tag.includes('COLLECTION_Personalised Product'));
            });
        }
        
        // Check for exact "Personalised" option name
        if (productData.options && Array.isArray(productData.options)) {
            productData.options.forEach(function(opt, index) {
                const optName = typeof opt === 'string' ? opt : (opt.name || opt);
                if (optName && optName.toLowerCase() === 'personalised') {
                    isPersonalised = true;
                    personalisationOptionIndex = index;
                }
            });
        }
        
        // For personalized products without "Personalised" option, don't show modal
        if (isPersonalised && personalisationOptionIndex === -1) {
            return;
        }
        
        // Find variant option index (prefer "Personalised" for personalized products, then "size", otherwise use first option)
        let variantOptionIndex = 0;
        if (isPersonalised && personalisationOptionIndex >= 0) {
            variantOptionIndex = personalisationOptionIndex;
        } else if (productData.options && Array.isArray(productData.options)) {
            const sizeIndex = productData.options.findIndex(function(opt) {
                // Handle both string options and object options
                const optionName = typeof opt === 'string' ? opt : (opt.name || opt);
                return optionName && optionName.toLowerCase() === 'size';
            });
            if (sizeIndex >= 0) {
                variantOptionIndex = sizeIndex;
            } else {
                // No size option, use first option (index 0)
                variantOptionIndex = 0;
            }
        }
        
        
        // Check if single variant product
        const isSingleVariant = productData.variants && productData.variants.length === 1 && 
                                productData.variants[0].title === "Default Title";
        
        // Build variant buttons (only if not single variant and has variants)
        if (productData.variants && productData.variants.length > 0 && !isSingleVariant) {
            // Reset display style for multi-variant products
            modalSizesContainer.style.display = '';
            let firstAvailableVariant = null;
            
            // For personalized products, only show Yes and No variants
            if (isPersonalised && personalisationOptionIndex >= 0) {
                let yesVariant = null;
                let noVariant = null;
                
                productData.variants.forEach(function(variant) {
                    let personalisationValue = null;
                    if (personalisationOptionIndex === 0 && variant.option1) {
                        personalisationValue = variant.option1;
                    } else if (personalisationOptionIndex === 1 && variant.option2) {
                        personalisationValue = variant.option2;
                    } else if (personalisationOptionIndex === 2 && variant.option3) {
                        personalisationValue = variant.option3;
                    } else if (variant.options && variant.options[personalisationOptionIndex]) {
                        personalisationValue = variant.options[personalisationOptionIndex];
                    }
                    
                    if (personalisationValue) {
                        const valueLower = personalisationValue.toLowerCase();
                        if (valueLower === 'yes' && !yesVariant) {
                            yesVariant = variant;
                        } else if (valueLower === 'no' && !noVariant) {
                            noVariant = variant;
                        }
                    }
                });
                
                // Create Yes button
                if (yesVariant) {
                    let isAvailable = true;
                    if (yesVariant.inventory_quantity !== undefined && yesVariant.inventory_quantity !== null) {
                        if (yesVariant.inventory_management === 'shopify' && yesVariant.inventory_policy === 'deny') {
                            isAvailable = yesVariant.inventory_quantity > 0;
                        } else {
                            isAvailable = true;
                        }
                    } else if (yesVariant.available !== undefined && yesVariant.available !== null) {
                        isAvailable = yesVariant.available === true || yesVariant.available === 'true' || yesVariant.available === 1 || yesVariant.available === '1';
                    }
                    
                    if (!firstAvailableVariant && isAvailable) {
                        firstAvailableVariant = yesVariant;
                    }
                    
                    const btn = document.createElement('button');
                    btn.type = 'button';
                    btn.className = 'bfs-quick-add-modal__size-btn';
                    if (!isAvailable) {
                        btn.classList.add('is-unavailable');
                        btn.disabled = true;
                    }
                    btn.textContent = 'Yes';
                    btn.setAttribute('data-variant-id', yesVariant.id);
                    btn.setAttribute('data-variant-available', isAvailable ? 'true' : 'false');
                    btn.setAttribute('data-product-handle', productHandle);
                    btn.setAttribute('data-is-personalised', 'true');
                    
                    btn.addEventListener('click', function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        const btnAvailable = this.getAttribute('data-variant-available') === 'true';
                        if (!btnAvailable) return;
                        
                        const btnProductHandle = this.getAttribute('data-product-handle');
                        if (btnProductHandle) {
                            window.location.href = '/products/' + btnProductHandle + '?variant=' + yesVariant.id;
                        }
                    });
                    
                    modalSizesContainer.appendChild(btn);
                }
                
                // Create No button
                if (noVariant) {
                    let isAvailable = true;
                    if (noVariant.inventory_quantity !== undefined && noVariant.inventory_quantity !== null) {
                        if (noVariant.inventory_management === 'shopify' && noVariant.inventory_policy === 'deny') {
                            isAvailable = noVariant.inventory_quantity > 0;
                        } else {
                            isAvailable = true;
                        }
                    } else if (noVariant.available !== undefined && noVariant.available !== null) {
                        isAvailable = noVariant.available === true || noVariant.available === 'true' || noVariant.available === 1 || noVariant.available === '1';
                    }
                    
                    if (!firstAvailableVariant && isAvailable) {
                        firstAvailableVariant = noVariant;
                    }
                    
                    const btn = document.createElement('button');
                    btn.type = 'button';
                    btn.className = 'bfs-quick-add-modal__size-btn';
                    if (!isAvailable) {
                        btn.classList.add('is-unavailable');
                        btn.disabled = true;
                    }
                    btn.textContent = 'No';
                    btn.setAttribute('data-variant-id', noVariant.id);
                    btn.setAttribute('data-variant-available', isAvailable ? 'true' : 'false');
                    btn.setAttribute('data-product-handle', productHandle);
                    btn.setAttribute('data-is-personalised', 'true');
                    
                    btn.addEventListener('click', function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        
                        const btnAvailable = this.getAttribute('data-variant-available') === 'true';
                        if (!btnAvailable) return;
                        
                        // Remove previous selection
                        modalSizesContainer.querySelectorAll('.bfs-quick-add-modal__size-btn').forEach(function(b) {
                            b.classList.remove('is-selected');
                        });
                        
                        // Add selection
                        btn.classList.add('is-selected');
                        currentSelectedVariantId = noVariant.id;
                        
                        // Add to cart immediately for "No"
                        addToCart(noVariant.id, btn, false).then(function() {
                            // Close modal after adding
                            setTimeout(function() {
                                closeModal();
                            }, 1000);
                        }).catch(function(error) {
                            console.error('Error adding to cart:', error);
                        });
                    });
                    
                    modalSizesContainer.appendChild(btn);
                }
            } else {
                // For non-personalized products, show all variants
                productData.variants.forEach(function(variant) {
                // Shopify variants use option1, option2, option3 properties
                let variantValue = null;
                if (variantOptionIndex === 0 && variant.option1) {
                    variantValue = variant.option1;
                } else if (variantOptionIndex === 1 && variant.option2) {
                    variantValue = variant.option2;
                } else if (variantOptionIndex === 2 && variant.option3) {
                    variantValue = variant.option3;
                } else if (variant.options && variant.options[variantOptionIndex]) {
                    // Fallback to options array if it exists
                    variantValue = variant.options[variantOptionIndex];
                }
                
                if (!variantValue) {
                    console.warn('Variant value not found for variant:', variant);
                    return; // Skip this variant if we can't find the value
                }
                
                // Check availability - Match product page logic: check inventory_quantity > 0
                // On live sites, the JSON API might return different data than Liquid
                let isAvailable = true; // Default to available
                
                // Priority 1: Check inventory_quantity (most reliable)
                // Match the product page logic: if inventory_quantity > 0, it's available
                if (variant.inventory_quantity !== undefined && variant.inventory_quantity !== null) {
                    // Check if inventory tracking is enabled
                    if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
                        // Tracked inventory with deny policy: must have quantity > 0
                        isAvailable = variant.inventory_quantity > 0;
                    } else if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'continue') {
                        // Tracked inventory with continue policy: always available (backorders allowed)
                        isAvailable = true;
                    } else {
                        // No inventory tracking or not set: assume available
                        isAvailable = true;
                    }
                } else if (variant.available !== undefined && variant.available !== null) {
                    // Priority 2: Use 'available' property if inventory_quantity is not present
                    // Handle both boolean and string values
                    isAvailable = variant.available === true || variant.available === 'true' || variant.available === 1 || variant.available === '1';
                }
                // If neither exists, default to true (assume available)
                const variantId = variant.id;
                
                
                // Track first available variant for auto-selection
                if (!firstAvailableVariant && isAvailable) {
                    firstAvailableVariant = variant;
                }
                
                const btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'bfs-quick-add-modal__size-btn';
                if (!isAvailable) {
                    btn.classList.add('is-unavailable');
                    btn.disabled = true;
                }
                btn.textContent = variantValue;
                btn.setAttribute('data-variant-id', variantId);
                btn.setAttribute('data-variant-available', isAvailable ? 'true' : 'false');
                btn.setAttribute('data-product-handle', productHandle);
                
                btn.addEventListener('click', function(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    
                    const btnAvailable = this.getAttribute('data-variant-available') === 'true';
                    if (!btnAvailable) return;
                    
                    const variantValueLower = this.textContent.trim().toLowerCase();
                    const btnIsPersonalised = this.getAttribute('data-is-personalised') === 'true';
                    const btnProductHandle = this.getAttribute('data-product-handle');
                    
                    // If personalized product and user clicked "Yes" or "No", navigate to PDP
                    if (btnIsPersonalised && (variantValueLower === 'yes' || variantValueLower === 'no')) {
                        if (btnProductHandle) {
                            window.location.href = '/products/' + btnProductHandle + '?variant=' + variantId;
                        } else {
                            console.error('Product handle not found for personalized variant');
                        }
                        return;
                    }
                    
                    // Remove previous selection
                    modalSizesContainer.querySelectorAll('.bfs-quick-add-modal__size-btn').forEach(function(b) {
                        b.classList.remove('is-selected');
                    });
                    
                    // Add selection
                    btn.classList.add('is-selected');
                    currentSelectedVariantId = variantId;
                    modalAddBtn.disabled = false;
                });
                
                modalSizesContainer.appendChild(btn);
            });
            
            // Auto-select first available variant and enable add to cart button
            if (firstAvailableVariant) {
                const firstBtn = modalSizesContainer.querySelector('[data-variant-id="' + firstAvailableVariant.id + '"]');
                if (firstBtn) {
                    firstBtn.classList.add('is-selected');
                    currentSelectedVariantId = firstAvailableVariant.id;
                    modalAddBtn.disabled = false;
                }
            }
        }
        } else if (isSingleVariant && productData.variants && productData.variants.length > 0) {
            // Single variant product - hide sizes container and auto-select the variant
            modalSizesContainer.style.display = 'none';
            const singleVariant = productData.variants[0];
            currentSelectedVariantId = singleVariant.id;
            modalAddBtn.disabled = false;
        } else {
            // No variants found, ensure button is disabled
            modalSizesContainer.style.display = '';
            modalAddBtn.disabled = true;
        }
        
        // Show/hide header and set title based on personalization
        if (modalHeader && modalTitle) {
            if (isPersonalised) {
                modalTitle.textContent = 'Add personalisation?';
                modalHeader.style.display = 'block';
            } else {
                modalHeader.style.display = 'none';
            }
        }
        
        // Reset modal button text and styles (disabled state is already set by auto-selection above)
        modalAddBtn.textContent = 'Add to cart';
        modalAddBtn.classList.remove('is-adding', 'is-added');
        modalAddBtn.style.background = '';
        
        // Show modal
        modal.setAttribute('aria-hidden', 'false');
        document.body.style.overflow = 'hidden';
    }
    
    // Close modal
    function closeModal() {
        if (!modal) return;
        modal.setAttribute('aria-hidden', 'true');
        document.body.style.overflow = '';
        currentSelectedVariantId = null;
        
        // Reset header visibility
        if (modalHeader) {
            modalHeader.style.display = 'none';
        }
    }
    
    // Close on Escape key
    document.addEventListener('keydown', function(e) {
        if (e.key === 'Escape' && modal && modal.getAttribute('aria-hidden') === 'false') {
            closeModal();
        }
    });

    // Update cart count if function exists
    function refreshCartCount() {
        if (typeof window.updateCart !== 'undefined') {
            window.updateCart();
        } else if (typeof Shopify !== 'undefined' && typeof Shopify.getCart !== 'undefined') {
            Shopify.getCart(function(cart) {
                // Trigger cart update event
                document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
            });
        }
    }

    // Desktop: "Size 12 added to cart" confirmation on the card image, with Undo
    function getAddedMessage(buttonElement, container) {
        if (!buttonElement.classList.contains('bfs-product-card__quick-add-size') || buttonElement.hasAttribute('data-personalise-choice')) {
            return 'Added to cart';
        }
        const sizesContainer = container.querySelector('.bfs-product-card__quick-add-sizes');
        let optionName = sizesContainer ? sizesContainer.getAttribute('data-option-name') : null;
        if (optionName === null) {
            // Boost-rendered cards don't carry the option name; short values are sizes
            optionName = sizesContainer && sizesContainer.classList.contains('bfs-product-card__quick-add-sizes--wide') ? '' : 'size';
        }
        const value = getFullLabel(buttonElement);
        return (optionName.toLowerCase() === 'size' ? 'Size ' + value : value) + ' added to cart';
    }

    function hideAddedToast(toast) {
        clearTimeout(toast.bfsTimer);
        toast.classList.remove('is-visible');
        if (toast.parentElement) {
            toast.parentElement.classList.remove('has-quick-add-toast');
        }
    }

    function showAddedToast(buttonElement, item) {
        const container = buttonElement.closest('.bfs-product-card__quick-add');
        const media = container ? container.closest('.bfs-product-card__media') : null;
        if (!media) return;
        
        let toast = media.querySelector('.bfs-product-card__quick-add-toast');
        if (!toast) {
            toast = document.createElement('div');
            toast.className = 'bfs-product-card__quick-add-toast';
            toast.setAttribute('role', 'status');
            toast.innerHTML = '<span class="bfs-product-card__quick-add-toast-message">' + ADDED_TOAST_ICON + '<span></span></span>' +
                '<button type="button" class="bfs-product-card__quick-add-toast-undo">Undo</button>';
            
            // The card is a link - keep clicks on the confirmation from navigating
            toast.addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
            });
            toast.querySelector('.bfs-product-card__quick-add-toast-undo').addEventListener('click', function(e) {
                e.preventDefault();
                e.stopPropagation();
                undoQuickAdd(toast);
            });
            media.appendChild(toast);
        }
        
        toast.bfsItem = item;
        toast.bfsContainer = container;
        toast.querySelector('.bfs-product-card__quick-add-toast-message > span').textContent = getAddedMessage(buttonElement, container);
        
        clearTimeout(toast.bfsTimer);
        media.classList.add('has-quick-add-toast');
        toast.classList.add('is-visible');
        toast.bfsTimer = setTimeout(function() {
            hideAddedToast(toast);
        }, ADDED_TOAST_TIMEOUT);
    }

    // Remove the one unit that was just added
    function undoQuickAdd(toast) {
        const item = toast.bfsItem;
        const undoBtn = toast.querySelector('.bfs-product-card__quick-add-toast-undo');
        if (!item || !item.key || undoBtn.disabled) {
            hideAddedToast(toast);
            return;
        }
        
        clearTimeout(toast.bfsTimer);
        undoBtn.disabled = true;
        
        fetch('/cart.js', {
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
        })
        .then(function(response) {
            return response.json();
        })
        .then(function(cart) {
            const line = cart.items.find(function(cartItem) {
                return cartItem.key === item.key;
            });
            if (!line) return cart;
            
            return fetch('/cart/change.js', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                },
                body: JSON.stringify({
                    id: item.key,
                    quantity: Math.max(0, line.quantity - 1)
                })
            }).then(function(response) {
                if (!response.ok) {
                    throw new Error('Failed to undo quick add');
                }
                return response.json();
            });
        })
        .then(function(cart) {
            if (toast.bfsContainer) {
                toast.bfsContainer.querySelectorAll('.bfs-product-card__quick-add-size.is-selected').forEach(function(b) {
                    b.classList.remove('is-selected');
                });
            }
            refreshCartCount();
            document.dispatchEvent(new CustomEvent('quickadd:undone', {
                detail: {
                    variantId: item.variant_id,
                    cart: cart
                }
            }));
        })
        .catch(function(error) {
            console.error('Quick Add Undo Error:', error);
        })
        .then(function() {
            undoBtn.disabled = false;
            hideAddedToast(toast);
        });
    }

    // Add to cart function (reusable)
    function addToCart(variantId, buttonElement, isMobileIcon) {
        if (!variantId) {
            console.error('No variant ID provided');
            return Promise.reject(new Error('No variant ID provided'));
        }
        
        const originalText = buttonElement ? buttonElement.textContent : '';
        
        if (buttonElement) {
            buttonElement.disabled = true;
            buttonElement.classList.add('is-adding');
            
            // For mobile icons and variant size buttons, just add the is-adding class (CSS handles spinner)
            const isVariantButton = buttonElement.classList.contains('bfs-product-card__quick-add-size');
            if (!isMobileIcon && !buttonElement.classList.contains('bfs-product-card__quick-add-icon') && !isVariantButton) {
                buttonElement.textContent = 'Adding...';
            }
        }
        
        return fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
                id: variantId,
                quantity: 1
            })
        })
        .then(function(response) {
            if (!response.ok) {
                return response.json().then(function(data) {
                    throw new Error(data.description || 'Failed to add to cart');
                });
            }
            return response.json();
        })
        .then(function(item) {
            // Success
            if (buttonElement) {
                buttonElement.classList.remove('is-adding');
                
                // For mobile icons and variant size buttons, just remove is-adding class (icon/text shows again)
                const isVariantButton = buttonElement.classList.contains('bfs-product-card__quick-add-size');
                if ((isMobileIcon && buttonElement.classList.contains('bfs-product-card__quick-add-icon')) || isVariantButton) {
                    // Icon/text is already visible (CSS animation stops), just enable button and restore selection state
                    buttonElement.disabled = false;
                    if (isVariantButton) {
                        // Keep the selected state for variant buttons
                        buttonElement.classList.add('is-selected');
                    }
                } else {
                    buttonElement.classList.add('is-added');
                    buttonElement.textContent = 'Added!';
                    
                    // Reset button after delay
                    setTimeout(function() {
                        buttonElement.classList.remove('is-added');
                        buttonElement.textContent = originalText;
                        buttonElement.disabled = false;
                    }, 2000);
                }
                
                // Desktop overlay: show the added confirmation on the card image
                if (buttonElement.closest('.bfs-product-card__quick-add')) {
                    showAddedToast(buttonElement, item);
                }
            }
            
            refreshCartCount();
            
            // Dispatch custom event for other scripts
            document.dispatchEvent(new CustomEvent('quickadd:added', {
                detail: {
                    variantId: variantId,
                    item: item
                }
            }));
            
            return item;
        })
        .catch(function(error) {
            // Error
            console.error('Quick Add Error:', error);
            if (buttonElement) {
                buttonElement.classList.remove('is-adding');
                
                // For mobile icons and variant size buttons, just remove is-adding class
                const isVariantButton = buttonElement.classList.contains('bfs-product-card__quick-add-size');
                if ((isMobileIcon && buttonElement.classList.contains('bfs-product-card__quick-add-icon')) || isVariantButton) {
                    // Icon/text is already visible, just enable button
                    buttonElement.disabled = false;
                } else {
                    buttonElement.textContent = 'Error';
                    buttonElement.style.background = '#dc3545';
                    
                    setTimeout(function() {
                        buttonElement.textContent = originalText;
                        buttonElement.style.background = '';
                        buttonElement.disabled = false;
                    }, 2000);
                }
            }
            throw error;
        });
    }

    // Overlay button clicks. theme.liquid has an inline capture-phase handler on document
    // that stops propagation (so the per-button listeners in initQuickAdd never fire) and
    // only knows the old "Yes"/"No" labels. Window capture runs before it, so handle
    // overlay buttons here.
    function handleOverlayButton(btn) {
        if (btn.disabled || btn.classList.contains('is-adding')) return;
        
        if (btn.classList.contains('bfs-product-card__quick-add-btn')) {
            addToCart(btn.getAttribute('data-single-variant-id'), btn).catch(function(error) {
                console.error('Quick Add Error:', error);
            });
            return;
        }
        
        const variantId = btn.getAttribute('data-variant-id');
        if (!variantId || btn.getAttribute('data-variant-available') !== 'true') return;
        
        const choice = (btn.getAttribute('data-personalise-choice') || btn.textContent).trim().toLowerCase();
        const productHandle = btn.getAttribute('data-product-handle');
        if (btn.getAttribute('data-is-personalised') === 'true' && choice === 'yes') {
            if (productHandle) {
                window.location.href = '/products/' + productHandle + '?variant=' + variantId;
            }
            return;
        }
        
        const siblings = btn.parentElement ? btn.parentElement.querySelectorAll('.bfs-product-card__quick-add-size') : [];
        siblings.forEach(function(b) {
            b.classList.remove('is-selected');
        });
        btn.classList.add('is-selected');
        
        addToCart(variantId, btn).catch(function(error) {
            console.error('Quick Add Error:', error);
        });
    }
    
    // Act on the click (a finished tap), never on touchstart: a touch that turns into a
    // scroll must not add to the cart. touchstart is only stopped from reaching the inline
    // handler, which acts on it straight away.
    function onOverlayButtonEvent(e) {
        const btn = e.target.closest ? e.target.closest('.bfs-product-card__quick-add-size, .bfs-product-card__quick-add-btn') : null;
        if (!btn || !btn.closest('.bfs-product-card__quick-add')) return;
        e.stopImmediatePropagation();
        if (e.type === 'click') {
            e.preventDefault();
            handleOverlayButton(btn);
        }
    }

    window.addEventListener('touchstart', onOverlayButtonEvent, { passive: true, capture: true });
    window.addEventListener('click', onOverlayButtonEvent, true);

    // Mobile: the "+" on multi-variant cards opens a bottom sheet to pick a size
    // (Figma 1035:11096). Single-variant "+" taps still add directly via the inline
    // script in theme.liquid.
    const SHEET_CLOSE_ICON = '<svg width="44" height="44" viewBox="0 0 44 44" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M15 15L22 22M22 22L29 29M22 22L29 15M22 22L15 29" stroke="black" stroke-width="2" stroke-linecap="round"/></svg>';
    let sheet = null;
    let sheetState = null;

    function formatSheetMoney(cents) {
        return '$' + (cents / 100).toFixed(2);
    }

    function sizedImageUrl(url, width) {
        if (!url) return '';
        if (url.indexOf('//') === 0) url = 'https:' + url;
        return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
    }

    function ensureSheet() {
        if (sheet) return sheet;
        sheet = document.createElement('div');
        sheet.className = 'bfs-quick-add-sheet';
        sheet.setAttribute('aria-hidden', 'true');
        sheet.innerHTML =
            '<div class="bfs-quick-add-sheet__overlay"></div>' +
            '<div class="bfs-quick-add-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="bfs-quick-add-sheet-title">' +
                '<div class="bfs-quick-add-sheet__grabber"><span></span></div>' +
                '<div class="bfs-quick-add-sheet__header">' +
                    '<h2 class="bfs-quick-add-sheet__title" id="bfs-quick-add-sheet-title">Add to cart</h2>' +
                    '<button type="button" class="bfs-quick-add-sheet__close" aria-label="Close">' + SHEET_CLOSE_ICON + '</button>' +
                '</div>' +
                '<div class="bfs-quick-add-sheet__summary">' +
                    '<a class="bfs-quick-add-sheet__image" href="#"><img alt="" width="72" height="108"></a>' +
                    '<div class="bfs-quick-add-sheet__info">' +
                        '<a class="bfs-quick-add-sheet__product-title" href="#"></a>' +
                        '<div class="bfs-quick-add-sheet__price"><span class="bfs-quick-add-sheet__sale-price"></span><s class="bfs-quick-add-sheet__compare-price"></s><span class="bfs-quick-add-sheet__discount"></span></div>' +
                        '<p class="bfs-quick-add-sheet__selection"></p>' +
                    '</div>' +
                '</div>' +
                '<div class="bfs-quick-add-sheet__divider"></div>' +
                '<div class="bfs-quick-add-sheet__options">' +
                    '<div class="bfs-quick-add-sheet__options-header">' +
                        '<div class="bfs-quick-add-sheet__options-label"><span class="bfs-quick-add-sheet__option-name"></span><span class="bfs-quick-add-sheet__option-value"></span></div>' +
                        '<button type="button" class="bfs-quick-add-sheet__size-guide" hidden>Size guide</button>' +
                    '</div>' +
                    '<div class="bfs-quick-add-sheet__chips"></div>' +
                    '<p class="bfs-quick-add-sheet__note"></p>' +
                '</div>' +
                '<div class="bfs-quick-add-sheet__footer"><button type="button" class="bfs-quick-add-sheet__cta" disabled></button></div>' +
                '<div class="bfs-quick-add-sheet__guide" hidden>' +
                    '<div class="bfs-quick-add-sheet__guide-header">' +
                        '<button type="button" class="bfs-quick-add-sheet__guide-back" aria-label="Back to sizes"><svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12.5 4L6.5 10L12.5 16" stroke="black" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg></button>' +
                        '<h3 class="bfs-quick-add-sheet__guide-title">Size guide</h3>' +
                    '</div>' +
                    '<div class="bfs-quick-add-sheet__guide-body"></div>' +
                '</div>' +
            '</div>';
        document.body.appendChild(sheet);

        sheet.querySelector('.bfs-quick-add-sheet__overlay').addEventListener('click', closeSheet);
        sheet.querySelector('.bfs-quick-add-sheet__close').addEventListener('click', closeSheet);
        sheet.querySelector('.bfs-quick-add-sheet__size-guide').addEventListener('click', openSizeGuide);
        sheet.querySelector('.bfs-quick-add-sheet__guide-back').addEventListener('click', closeSizeGuide);
        sheet.querySelector('.bfs-quick-add-sheet__cta').addEventListener('click', addFromSheet);
        sheet.querySelector('.bfs-quick-add-sheet__chips').addEventListener('click', function(e) {
            const chip = e.target.closest('.bfs-quick-add-sheet__chip');
            if (chip && !chip.disabled) selectSheetValue(Number(chip.getAttribute('data-index')));
        });
        document.addEventListener('keydown', function(e) {
            if (e.key === 'Escape' && sheet.getAttribute('aria-hidden') === 'false') closeSheet();
        });

        // Swipe down on the grabber/header to dismiss
        const panel = sheet.querySelector('.bfs-quick-add-sheet__panel');
        let dragStartY = null;
        let dragDistance = 0;
        [sheet.querySelector('.bfs-quick-add-sheet__grabber'), sheet.querySelector('.bfs-quick-add-sheet__header')].forEach(function(handle) {
            handle.addEventListener('touchstart', function(e) {
                dragStartY = e.touches[0].clientY;
                dragDistance = 0;
                panel.style.transition = 'none';
            }, { passive: true });
        });
        panel.addEventListener('touchmove', function(e) {
            if (dragStartY === null) return;
            dragDistance = Math.max(0, e.touches[0].clientY - dragStartY);
            panel.style.transform = 'translateY(' + dragDistance + 'px)';
        }, { passive: true });
        panel.addEventListener('touchend', function() {
            if (dragStartY === null) return;
            dragStartY = null;
            panel.style.transition = '';
            panel.style.transform = '';
            if (dragDistance > 80) closeSheet();
        });

        return sheet;
    }

    // "Select a size" reads well; other option names (Packs, Colour) get a neutral prompt
    function sheetPrompt(optionName) {
        return optionName.toLowerCase() === 'size' ? 'Select a size' : 'Select an option';
    }

    function setSheetCta(text, enabled) {
        const cta = sheet.querySelector('.bfs-quick-add-sheet__cta');
        cta.textContent = text;
        cta.disabled = !enabled;
    }

    // Product JSON per handle, so reopening the sheet never waits
    const sheetProductCache = {};

    function fetchSheetProduct(handle) {
        if (!sheetProductCache[handle]) {
            sheetProductCache[handle] = fetch('/products/' + handle + '.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
                .then(function(response) {
                    if (!response.ok) throw new Error('Product not found');
                    return response.json();
                })
                .catch(function(error) {
                    delete sheetProductCache[handle];
                    throw error;
                });
        }
        return sheetProductCache[handle];
    }

    // Read what the card already shows (image, title, price, quick add buttons) so the
    // sheet opens without waiting for the network
    function getCardSheetData(icon) {
        const card = icon.closest('.bfs-product-card');
        const overlay = card ? card.querySelector('.bfs-product-card__quick-add') : null;
        const buttons = overlay ? overlay.querySelectorAll('.bfs-product-card__quick-add-size') : [];
        if (!buttons.length) return null;

        // One chip per option value; it's available if any variant with that value is
        const values = [];
        buttons.forEach(function(btn) {
            const value = getFullLabel(btn);
            const id = Number(btn.getAttribute('data-variant-id'));
            const available = btn.getAttribute('data-variant-available') === 'true';
            let entry = values.find(function(v) { return v.value === value; });
            if (!entry) {
                entry = { value: value, variant: { id: id }, available: false };
                values.push(entry);
            }
            if (available && !entry.available) {
                entry.available = true;
                entry.variant = { id: id };
            }
        });

        const img = card.querySelector('.bfs-product-card__media img');
        const title = card.querySelector('.bfs-product-card__title');
        const price = card.querySelector('.bfs-product-card__price');
        const salePrice = price ? price.querySelector('.bfs-product-card__sale-price, span') : null;
        const comparePrice = price ? price.querySelector('s') : null;
        const sizesContainer = overlay.querySelector('.bfs-product-card__quick-add-sizes');
        return {
            title: title ? title.textContent.trim() : '',
            url: card.getAttribute('href') || '/products/' + icon.getAttribute('data-product-handle'),
            image: img ? (img.currentSrc || img.src) : '',
            optionName: (sizesContainer && sizesContainer.getAttribute('data-option-name')) || 'Size',
            values: values,
            priceText: salePrice ? salePrice.textContent.trim() : '',
            compareText: comparePrice ? comparePrice.textContent.trim() : ''
        };
    }

    // Same shape from the product JSON, for cards without quick add buttons to read
    function getProductSheetData(product) {
        const optionNames = (product.options || []).map(function(opt) {
            return typeof opt === 'string' ? opt : (opt.name || '');
        });
        let optionIndex = optionNames.findIndex(function(name) {
            return name.toLowerCase() === 'size';
        });
        if (optionIndex < 0) optionIndex = 0;

        const values = [];
        product.variants.forEach(function(variant) {
            const value = (variant.options && variant.options[optionIndex]) || variant['option' + (optionIndex + 1)];
            if (!value) return;
            let entry = values.find(function(v) { return v.value === value; });
            if (!entry) {
                entry = { value: value, variant: variant, available: false };
                values.push(entry);
            }
            if (variant.available && !entry.available) {
                entry.available = true;
                entry.variant = variant;
            }
        });

        const firstAvailable = values.find(function(v) { return v.available; });
        const priceVariant = firstAvailable ? firstAvailable.variant : product.variants[0];
        const onSale = priceVariant.compare_at_price && priceVariant.compare_at_price > priceVariant.price;
        return {
            title: product.title,
            url: '/products/' + product.handle,
            image: sizedImageUrl(product.featured_image, 200),
            optionName: optionNames[optionIndex] || 'Size',
            values: values,
            priceText: formatSheetMoney(priceVariant.price),
            compareText: onSale ? formatSheetMoney(priceVariant.compare_at_price) : ''
        };
    }

    function openSheet(icon) {
        const handle = icon.getAttribute('data-product-handle');
        if (!handle) return;
        ensureSheet();
        sheetState = { handle: handle, values: [], selected: -1, optionName: 'Size', sizeGuide: null };
        sheet.querySelector('.bfs-quick-add-sheet__size-guide').hidden = true;
        sheet.setAttribute('aria-hidden', 'false');
        document.documentElement.classList.add('bfs-quick-add-sheet-open');

        const cardData = getCardSheetData(icon);
        if (cardData) {
            renderSheet(cardData);
        } else {
            sheet.classList.add('is-loading');
            sheet.querySelector('.bfs-quick-add-sheet__chips').innerHTML = '';
            sheet.querySelector('.bfs-quick-add-sheet__note').textContent = '';
            setSheetCta('Loading…', false);
        }

        // The full product adds per-variant prices, or renders the sheet when the card had nothing to read
        fetchSheetProduct(handle)
            .then(function(product) {
                if (!sheetState || sheetState.handle !== handle) return;
                if (cardData) {
                    applySheetProduct(product);
                } else {
                    renderSheet(getProductSheetData(product));
                }
                applySizeGuide(product);
            })
            .catch(function(error) {
                console.error('Quick Add Sheet Error:', error);
                if (!cardData) window.location.href = '/products/' + handle;
            });
    }

    function applySheetProduct(product) {
        sheetState.values.forEach(function(entry) {
            const variant = product.variants.find(function(v) { return v.id === entry.variant.id; });
            if (variant) entry.variant = variant;
        });
        const img = sheet.querySelector('.bfs-quick-add-sheet__image img');
        if (!img.getAttribute('src')) img.src = sizedImageUrl(product.featured_image, 200);

        // Boost-rendered cards don't carry the option name; take the real one from the product
        const sample = sheetState.values[0] && product.variants.find(function(v) { return v.id === sheetState.values[0].variant.id; });
        const optionIndex = sample ? sample.options.indexOf(sheetState.values[0].value) : -1;
        const opt = optionIndex >= 0 && product.options ? product.options[optionIndex] : null;
        const optionName = opt ? (typeof opt === 'string' ? opt : opt.name) : '';
        if (optionName && optionName !== sheetState.optionName) {
            sheetState.optionName = optionName;
            sheet.querySelector('.bfs-quick-add-sheet__option-name').textContent = optionName;
            const isSize = optionName.toLowerCase() === 'size';
            const current = sheetState.values[sheetState.selected];
            const selection = sheet.querySelector('.bfs-quick-add-sheet__selection');
            if (current) {
                selection.textContent = (isSize ? 'Size ' : '') + current.value;
            } else if (sheetState.values.some(function(v) { return v.available; })) {
                selection.textContent = sheetPrompt(optionName);
                setSheetCta(sheetPrompt(optionName), false);
            }
        }

        const selected = sheetState.values[sheetState.selected];
        if (selected) updateSheetPrice(selected.variant);
    }

    function closeSheet() {
        if (!sheet) return;
        sheet.setAttribute('aria-hidden', 'true');
        document.documentElement.classList.remove('bfs-quick-add-sheet-open');
        sheetState = null;
        closeSizeGuide();
    }

    // Size guide: like the product page, a "__tab2:size-chart-…" tag names the size chart
    // page. templates/page.quick-add.liquid returns just that page's content.
    const sizeGuideCache = {};

    function getSizeGuideHandle(product) {
        const tags = product.tags || [];
        for (let i = 0; i < tags.length; i++) {
            const match = /^__tab\d+:(.*size-chart.*)$/.exec(tags[i]);
            if (match) return match[1];
        }
        return null;
    }

    function fetchSizeGuide(handle) {
        if (!sizeGuideCache[handle]) {
            sizeGuideCache[handle] = fetch('/pages/' + handle + '?view=quick-add')
                .then(function(response) {
                    if (!response.ok) throw new Error('Size guide not found');
                    return response.text();
                })
                .catch(function(error) {
                    delete sizeGuideCache[handle];
                    throw error;
                });
        }
        return sizeGuideCache[handle];
    }

    function applySizeGuide(product) {
        const handle = getSizeGuideHandle(product);
        sheetState.sizeGuide = handle;
        sheet.querySelector('.bfs-quick-add-sheet__size-guide').hidden = !handle;
        // Load it now so it opens instantly
        if (handle) fetchSizeGuide(handle).catch(function() {});
    }

    function openSizeGuide() {
        const handle = sheetState && sheetState.sizeGuide;
        if (!handle) return;
        const body = sheet.querySelector('.bfs-quick-add-sheet__guide-body');
        body.textContent = 'Loading…';
        sheet.querySelector('.bfs-quick-add-sheet__guide').hidden = false;
        sheet.classList.add('is-guide-open');

        fetchSizeGuide(handle)
            .then(function(html) {
                if (!sheetState || sheetState.sizeGuide !== handle) return;
                body.innerHTML = html;
                body.scrollTop = 0;
            })
            .catch(function(error) {
                console.error('Quick Add Size Guide Error:', error);
                if (!sheetState || sheetState.sizeGuide !== handle) return;
                body.innerHTML = '<p>The size guide couldn\'t load. <a href="/pages/' + handle + '">Open it on its own page</a>.</p>';
            });
    }

    function closeSizeGuide() {
        if (!sheet) return;
        sheet.classList.remove('is-guide-open');
        sheet.querySelector('.bfs-quick-add-sheet__guide').hidden = true;
    }

    function renderSheet(data) {
        const values = data.values;
        const optionName = data.optionName;
        sheetState.values = values;
        sheetState.optionName = optionName;
        sheetState.selected = -1;

        const imageLink = sheet.querySelector('.bfs-quick-add-sheet__image');
        const titleLink = sheet.querySelector('.bfs-quick-add-sheet__product-title');
        imageLink.href = data.url;
        titleLink.href = data.url;
        titleLink.textContent = data.title;
        imageLink.querySelector('img').src = data.image;

        const isSize = optionName.toLowerCase() === 'size';
        sheet.querySelector('.bfs-quick-add-sheet__option-name').textContent = optionName;
        sheet.querySelector('.bfs-quick-add-sheet__option-value').textContent = '';

        const chips = sheet.querySelector('.bfs-quick-add-sheet__chips');
        chips.classList.toggle('bfs-quick-add-sheet__chips--wide', values.some(function(v) { return v.value.length > 3; }));
        chips.innerHTML = values.map(function(v, i) {
            return '<button type="button" class="bfs-quick-add-sheet__chip' + (v.available ? '' : ' is-unavailable') + '" data-index="' + i + '" aria-pressed="false"' + (v.available ? '' : ' disabled') + '></button>';
        }).join('');
        chips.querySelectorAll('.bfs-quick-add-sheet__chip').forEach(function(chip, i) {
            // "S - D16cm x H14cm" becomes a two-line chip: code on top, details underneath
            const value = values[i].value;
            const split = value.indexOf(' - ');
            if (split > 0) {
                chip.classList.add('bfs-quick-add-sheet__chip--detail');
                chip.innerHTML = '<span class="bfs-quick-add-sheet__chip-code"></span><span class="bfs-quick-add-sheet__chip-detail"></span>';
                chip.firstChild.textContent = value.slice(0, split);
                chip.lastChild.textContent = value.slice(split + 3);
            } else {
                chip.textContent = value;
            }
        });

        const soldOut = values.filter(function(v) { return !v.available; }).map(function(v) { return v.value; });
        const note = sheet.querySelector('.bfs-quick-add-sheet__note');
        note.classList.remove('is-error');
        if (soldOut.length === values.length) {
            note.textContent = isSize ? 'All sizes are sold out.' : 'All options are sold out.';
        } else if (soldOut.length) {
            const list = soldOut.length > 1 ? soldOut.slice(0, -1).join(', ') + ' and ' + soldOut[soldOut.length - 1] : soldOut[0];
            note.textContent = (isSize ? (soldOut.length > 1 ? 'Sizes ' : 'Size ') : '') + list + (soldOut.length > 1 ? ' are' : ' is') + ' sold out.';
        } else {
            note.textContent = '';
        }

        setSheetPrice(data.priceText, data.compareText);
        sheet.querySelector('.bfs-quick-add-sheet__selection').textContent = soldOut.length === values.length ? 'Sold out' : sheetPrompt(optionName);
        setSheetCta(soldOut.length === values.length ? 'Sold out' : sheetPrompt(optionName), false);
        sheet.classList.remove('is-loading');
    }

    function setSheetPrice(priceText, compareText) {
        const sale = sheet.querySelector('.bfs-quick-add-sheet__sale-price');
        const compare = sheet.querySelector('.bfs-quick-add-sheet__compare-price');
        sale.textContent = priceText;
        sale.classList.toggle('is-sale', !!compareText);
        compare.textContent = compareText;

        // Same rounding as the card's "20% OFF" in bfs-product-card.liquid
        const price = parseFloat(priceText.replace(/[^0-9.]/g, ''));
        const was = parseFloat(compareText.replace(/[^0-9.]/g, ''));
        const percent = was > price ? Math.round((was - price) * 100 / was) : 0;
        sheet.querySelector('.bfs-quick-add-sheet__discount').textContent = percent > 0 ? percent + '% OFF' : '';
    }

    function updateSheetPrice(variant) {
        // Card data only has variant IDs; keep the card's price until the product JSON arrives
        if (typeof variant.price !== 'number') return;
        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        setSheetPrice(formatSheetMoney(variant.price), onSale ? formatSheetMoney(variant.compare_at_price) : '');
    }

    function selectSheetValue(index) {
        const entry = sheetState && sheetState.values[index];
        if (!entry || !entry.available) return;
        sheetState.selected = index;

        sheet.querySelectorAll('.bfs-quick-add-sheet__chip').forEach(function(chip, i) {
            chip.classList.toggle('is-selected', i === index);
            chip.setAttribute('aria-pressed', i === index ? 'true' : 'false');
        });
        const isSize = sheetState.optionName.toLowerCase() === 'size';
        sheet.querySelector('.bfs-quick-add-sheet__option-value').textContent = entry.value;
        sheet.querySelector('.bfs-quick-add-sheet__selection').textContent = (isSize ? 'Size ' : '') + entry.value;
        updateSheetPrice(entry.variant);
        setSheetCta('Add to cart', true);
    }

    function addFromSheet() {
        const entry = sheetState && sheetState.values[sheetState.selected];
        const cta = sheet.querySelector('.bfs-quick-add-sheet__cta');
        if (!entry || cta.disabled) return;

        setSheetCta('Adding…', false);
        fetch('/cart/add.js', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({ id: entry.variant.id, quantity: 1 })
        })
        .then(function(response) {
            return response.json().then(function(data) {
                if (!response.ok) throw new Error(data.description || 'Failed to add to cart');
                return data;
            });
        })
        .then(function(item) {
            cta.classList.add('is-added');
            setSheetCta('Added to cart', false);
            refreshCartCount();
            document.dispatchEvent(new CustomEvent('quickadd:added', {
                detail: {
                    variantId: entry.variant.id,
                    item: item
                }
            }));
            setTimeout(function() {
                cta.classList.remove('is-added');
                closeSheet();
            }, 900);
        })
        .catch(function(error) {
            console.error('Quick Add Error:', error);
            const note = sheet.querySelector('.bfs-quick-add-sheet__note');
            note.textContent = error.message;
            note.classList.add('is-error');
            setSheetCta('Add to cart', true);
        });
    }

    // Window capture runs before the inline theme.liquid handler, which acts on touchstart
    // (adding single-variant products and blocking the scroll as soon as a thumb lands on
    // the "+"). Let the tap become a click so scrolling still works, then act on the click:
    // single-variant products add straight away, the rest open the sheet.
    function onMobileIconEvent(e) {
        const icon = e.target.closest ? e.target.closest('.bfs-product-card__quick-add-icon') : null;
        if (!icon) return;
        e.stopImmediatePropagation();
        const singleVariantId = icon.getAttribute('data-single-variant-id');
        if (e.type === 'touchstart' || e.type === 'mousedown') {
            // Warm the product cache before the click fires
            if (!singleVariantId) fetchSheetProduct(icon.getAttribute('data-product-handle')).catch(function() {});
        } else if (e.type === 'click') {
            e.preventDefault();
            if (singleVariantId) {
                if (!icon.classList.contains('is-adding')) {
                    addToCart(singleVariantId, icon, true).catch(function(error) {
                        console.error('Quick Add Error:', error);
                    });
                }
            } else {
                openSheet(icon);
            }
        }
    }

    window.addEventListener('touchstart', onMobileIconEvent, { passive: true, capture: true });
    window.addEventListener('mousedown', onMobileIconEvent, true);
    window.addEventListener('mouseup', onMobileIconEvent, true);
    window.addEventListener('click', onMobileIconEvent, true);

    // Kept for backward compatibility
    function initMobileQuickAddIcons() {
        // Mobile icon taps are handled by onMobileIconEvent above
    }

    // Initialize Quick Add functionality
    function initQuickAdd() {
        loadQuickAddStyles();
        
        // First, clear initialization flags for containers that might have been replaced
        // This helps when boost-pfs rebuilds products
        const allContainers = document.querySelectorAll('.bfs-product-card__quick-add[data-initialized]');
        allContainers.forEach(function(container) {
            // Check if container still has buttons (might have been replaced by boost-pfs)
            const hasButtons = container.querySelector('.bfs-product-card__quick-add-size') || container.querySelector('.bfs-product-card__quick-add-btn');
            const hasSizesContainer = container.querySelector('.bfs-product-card__quick-add-sizes');
            const isPersonalised = container.getAttribute('data-is-personalised') === 'true';
            
            // If personalized product container exists but has no buttons, check if it should have them
            if (isPersonalised && hasSizesContainer && !hasButtons) {
                // Personalized product should have buttons if it has Personalised option
                // Clear flag to allow re-initialization - might be a timing issue
                container.removeAttribute('data-initialized');
            } else if (!isPersonalised && hasSizesContainer && !hasButtons) {
                // Non-personalized product with sizes container but no buttons - might have been replaced
                container.removeAttribute('data-initialized');
            }
            // Also check if the container is inside a product card that was just added
            const productCard = container.closest('.bfs-product-card');
            if (productCard && !productCard.hasAttribute('data-quick-add-initialized')) {
                productCard.setAttribute('data-quick-add-initialized', 'true');
                container.removeAttribute('data-initialized');
            }
        });
        
        const quickAddContainers = document.querySelectorAll('.bfs-product-card__quick-add:not([data-initialized])');
        
        quickAddContainers.forEach(function(container) {
            normaliseQuickAddMarkup(container);
            
            // Skip if container is empty or doesn't have the expected structure
            const hasSizesContainer = container.querySelector('.bfs-product-card__quick-add-sizes');
            const hasButtons = container.querySelector('.bfs-product-card__quick-add-size') || container.querySelector('.bfs-product-card__quick-add-btn');
            const isPersonalised = container.getAttribute('data-is-personalised') === 'true';
            
            // If personalized product container exists but has no buttons, check if it should be empty
            if (isPersonalised && !hasButtons && hasSizesContainer) {
                // Check if sizes container is intentionally empty (personalized without Personalised option)
                const sizesContainer = container.querySelector('.bfs-product-card__quick-add-sizes');
                if (sizesContainer && sizesContainer.children.length === 0) {
                    // This is a personalized product without Personalised option - should be empty, skip initialization
                    container.setAttribute('data-initialized', 'true');
                    return;
                }
            }
            
            // If container has no buttons and no sizes container, it might be malformed - skip it
            if (!hasButtons && !hasSizesContainer) {
                container.setAttribute('data-initialized', 'true');
                return;
            }
            
            // Mark as initialized to prevent double-binding
            container.setAttribute('data-initialized', 'true');
            
            const productId = container.getAttribute('data-product-id');
            const sizeButtons = container.querySelectorAll('.bfs-product-card__quick-add-size');
            const addToCartBtn = container.querySelector('.bfs-product-card__quick-add-btn');
            
            let selectedVariantId = null;
            
            // Check if single variant product
            const singleVariantId = addToCartBtn ? addToCartBtn.getAttribute('data-single-variant-id') : null;
            
            if (singleVariantId && addToCartBtn) {
                // Single variant product - use the variant ID directly
                selectedVariantId = singleVariantId;
                addToCartBtn.disabled = false;
            } else if (sizeButtons.length > 0) {
                // Multi-variant product (no Quick Add button) - set up direct add to cart on variant click
                const isPersonalised = container.getAttribute('data-is-personalised') === 'true';
                const productHandle = container.getAttribute('data-product-handle');
                
                sizeButtons.forEach(function(btn) {
                    btn.addEventListener('click', function(e) {
                        e.preventDefault();
                        e.stopPropagation();
                        e.stopImmediatePropagation();
                        
                        const variantId = this.getAttribute('data-variant-id');
                        const isAvailable = this.getAttribute('data-variant-available') === 'true';
                        // Personalised buttons are labelled "Personalise" / "Add To Cart"; the choice is in data-personalise-choice
                        const variantValue = (this.getAttribute('data-personalise-choice') || this.textContent).trim().toLowerCase();
                        const btnIsPersonalised = this.getAttribute('data-is-personalised') === 'true';
                        const btnProductHandle = this.getAttribute('data-product-handle') || productHandle;
                        
                        if (!isAvailable || !variantId) {
                            return false;
                        }
                        
                        // Check if this is a personalized product
                        if ((isPersonalised || btnIsPersonalised)) {
                            if (variantValue === 'yes') {
                                // Navigate to product page with variant selected
                                if (btnProductHandle) {
                                    window.location.href = '/products/' + btnProductHandle + '?variant=' + variantId;
                                } else {
                                    console.error('Product handle not found for personalized variant');
                                }
                                return false;
                            } else if (variantValue === 'no') {
                                // For "No", add to cart directly (if in stock)
                                // Remove previous selection
                                sizeButtons.forEach(function(b) {
                                    b.classList.remove('is-selected');
                                });
                                
                                // Add selection to clicked button
                                this.classList.add('is-selected');
                                
                                // Add to cart directly
                                addToCart(variantId, this).catch(function(error) {
                                    console.error('Quick Add Error:', error);
                                });
                                
                                return false;
                            }
                        }
                        
                        // Remove previous selection
                        sizeButtons.forEach(function(b) {
                            b.classList.remove('is-selected');
                        });
                        
                        // Add selection to clicked button
                        this.classList.add('is-selected');
                        
                        // Add to cart directly (for non-personalized variants)
                        addToCart(variantId, this).catch(function(error) {
                            console.error('Quick Add Error:', error);
                        });
                        
                        return false;
                    });
                });
            }
            
            // Handle add to cart (only if button exists - single variant products)
            if (addToCartBtn) {
                addToCartBtn.addEventListener('click', function(e) {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    
                    if (!selectedVariantId || this.disabled || this.classList.contains('is-adding')) {
                        return false;
                    }
                    
                    // Use reusable addToCart function
                    addToCart(selectedVariantId, this).catch(function(error) {
                        console.error('Quick Add Error:', error);
                    });
                    return false;
                });
            }
            
            // Prevent card link from firing when clicking on quick add container (but allow buttons to work)
            container.addEventListener('click', function(e) {
                // Only prevent if clicking on the container itself, not on buttons inside
                if (e.target === container || (!e.target.closest('.bfs-product-card__quick-add-size') && !e.target.closest('.bfs-product-card__quick-add-btn'))) {
                    e.stopPropagation();
                }
            });
        });
    }
    
    // Initialize on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
            initQuickAdd();
            initMobileQuickAddIcons();
        });
    } else {
        initQuickAdd();
        initMobileQuickAddIcons();
    }
    
    // Re-initialize for dynamically loaded content (e.g., AJAX filters)
    document.addEventListener('DOMContentLoaded', function() {
        // Use MutationObserver to watch for new product cards
        const observer = new MutationObserver(function(mutations) {
            let shouldReinit = false;
            mutations.forEach(function(mutation) {
                // Check for added nodes
                mutation.addedNodes.forEach(function(node) {
                    if (node.nodeType === 1) {
                        if (node.classList && node.classList.contains('bfs-product-card__quick-add')) {
                            shouldReinit = true;
                        } else if (node.querySelector && node.querySelector('.bfs-product-card__quick-add')) {
                            shouldReinit = true;
                        } else if (node.classList && node.classList.contains('bfs-product-card')) {
                            shouldReinit = true;
                        }
                    }
                });
                // Also check for removed nodes (HTML replacement)
                if (mutation.removedNodes && mutation.removedNodes.length > 0) {
                    mutation.removedNodes.forEach(function(node) {
                        if (node.nodeType === 1 && node.classList && node.classList.contains('bfs-product-card__quick-add')) {
                            shouldReinit = true;
                        }
                    });
                }
            });
            
            if (shouldReinit) {
                // Small delay to ensure DOM is fully updated
                setTimeout(function() {
                    initQuickAdd();
                    initMobileQuickAddIcons();
                }, 200);
            }
        });
        
        const container = document.body;
        if (container) {
            observer.observe(container, {
                childList: true,
                subtree: true,
                attributes: false,
                characterData: false
            });
        }
        
        // Also do a periodic check for the first few seconds after page load
        let checkCount = 0;
        const maxChecks = 5;
        const checkInterval = setInterval(function() {
            checkCount++;
            const uninitialized = document.querySelectorAll('.bfs-product-card__quick-add:not([data-initialized])');
            if (uninitialized.length > 0) {
                initQuickAdd();
                initMobileQuickAddIcons();
            }
            if (checkCount >= maxChecks) {
                clearInterval(checkInterval);
            }
        }, 1000);
    });
    
    // Listen for boost-pfs events and other dynamic content events
    if (typeof window !== 'undefined') {
        // Function to re-initialize with multiple attempts
        function reinitQuickAdd() {
            setTimeout(function() {
                initQuickAdd();
                initMobileQuickAddIcons();
            }, 100);
            setTimeout(function() {
                initQuickAdd();
                initMobileQuickAddIcons();
            }, 300);
            setTimeout(function() {
                initQuickAdd();
                initMobileQuickAddIcons();
            }, 600);
        }
        
        window.addEventListener('boost-pfs-filter-products-loaded', reinitQuickAdd);
        window.addEventListener('boost-pfs-filter-render-complete', reinitQuickAdd);
        window.addEventListener('boost-pfs-ajax-complete', reinitQuickAdd);
        window.addEventListener('boost-pfs-filter-after-processing', reinitQuickAdd);
        
        // Re-initialize after window load to catch any products that might have been loaded
        window.addEventListener('load', function() {
            setTimeout(function() {
                initQuickAdd();
                initMobileQuickAddIcons();
            }, 500);
            setTimeout(function() {
                initQuickAdd();
                initMobileQuickAddIcons();
            }, 1000);
            setTimeout(function() {
                initQuickAdd();
                initMobileQuickAddIcons();
            }, 2000);
        });
        
        // Also listen for any DOM mutations that might indicate product cards were replaced
        var loadObserver = new MutationObserver(function(mutations) {
            var shouldReinit = false;
            mutations.forEach(function(mutation) {
                if (mutation.addedNodes.length > 0) {
                    for (var i = 0; i < mutation.addedNodes.length; i++) {
                        var node = mutation.addedNodes[i];
                        if (node.nodeType === 1 && node.querySelector && node.querySelector('.bfs-product-card__quick-add')) {
                            shouldReinit = true;
                            break;
                        }
                    }
                }
            });
            if (shouldReinit) {
                setTimeout(function() {
                    initQuickAdd();
                    initMobileQuickAddIcons();
                }, 200);
            }
        });
        
        // Start observing after a short delay to avoid interfering with initial load
        setTimeout(function() {
            if (document.body) {
                loadObserver.observe(document.body, {
                    childList: true,
                    subtree: true
                });
            }
        }, 2000);
    }
    
})();
